import { useState } from "react";
import { Table, Button, OverlayTrigger, Tooltip } from "react-bootstrap";
import { FaEdit, FaClock } from "react-icons/fa";
import {
  HourglassSplit,
  SendFill,
  Search,
  ArrowClockwise,
  CalendarX,
  CheckCircleFill,
  XCircleFill,
  ExclamationCircle,
  PenFill,
} from "react-bootstrap-icons";
import { useNavigate } from "react-router-dom";
import { getSindicatoById } from "../../../services/index";
import { formatCnpj, formatDateTimeBR, normalizeText } from "../../../utils";
import { PATHS } from "../../../routes/paths";
import type { PaymentListRow } from "../V2/mapComprovantesSolicitacoesToPaymentRows";
import "./PaymentTable.css";

const TOOLTIP_AGUARDANDO_SINDICATO =
  "Aguardando o envio dos comprovantes pelo sindicato.";

const getSituationConfig = (situacaoRaw: string | undefined) => {
  const s = normalizeText(situacaoRaw);

  if (s.includes("comprovantes pendente")) {
    return { icon: <HourglassSplit />, className: "status-pendente-sindicato" };
  }

  if (s.includes("comprovantes enviado")) {
    return { icon: <SendFill />, className: "status-enviado" };
  }

  if (s.includes("conferencia pendente")) {
    return { icon: <Search />, className: "status-pendente-analista" };
  }

  if (s.includes("conferencia em andamento")) {
    return { icon: <ArrowClockwise />, className: "status-andamento" };
  }

  if (s.includes("correcao pendente")) {
    return { icon: <HourglassSplit />, className: "status-adequacao-recusa" };
  }

  if (s.includes("assinatura pendente")) {
    return { icon: <PenFill />, className: "status-adequacao-recusa" };
  }

  if (s.includes("assinatura expirada")) {
    return { icon: <CalendarX />, className: "status-inapto" };
  }

  if (
    s.includes("conferencia concluida") ||
    s.includes("assinatura concluida")
  ) {
    return { icon: <CheckCircleFill color="#067647" />, className: "status-plain" };
  }

  if (s.includes("inapto")) {
    return { icon: <XCircleFill color="#BA0C2F" />, className: "status-plain" };
  }

  return { icon: <ExclamationCircle />, className: "status-default" };
};

type PaymentTableProps = {
  currentItems: PaymentListRow[];
  handleSort: (key: string) => void;
  getSortIcon: (key: string) => string;
  onOpenHistory: (item: PaymentListRow) => void;
  selectedYear?: number;
};

const PaymentTable = ({
  currentItems,
  handleSort,
  getSortIcon,
  onOpenHistory,
  selectedYear,
}: PaymentTableProps) => {
  const navigate = useNavigate();
  const [tooltipContent, setTooltipContent] = useState("");

  const handleEdit = (item: PaymentListRow) => {
    if (!item.acaoHabilitada) return;

    const id = item.idSindicato ?? item.id;
    navigate(
      PATHS.EXTRACT_CONF_COMP.replace(":idSindicato", String(id)),
      {
        state: {
          selectedYear: selectedYear ?? item.ano,
          extratoId: item.extratoId,
          solicitacaoId: item.extratoId,
          solicitacao: item.solicitacaoLabel,
          situacao: item.situacao,
        },
      },
    );
  };

  const handleTooltip = async (id: number) => {
    try {
      const response = await getSindicatoById(id);
      setTooltipContent(response?.data?.nome || "-");
    } catch {
      setTooltipContent("Erro ao carregar nome");
    }
  };

  return (
    <div className="table-responsive-wrapper">
      <Table bordered hover className="payment-table">
        <colgroup>
          <col className="col-cnpj" />
          <col className="col-sigla" />
          <col className="col-solicitacao-label" />
          <col className="col-situacao" />
          <col className="col-envio" />
          <col className="col-solicitacao" />
          <col className="col-correcao" />
          <col className="col-envio-formulario" />
          <col className="col-acoes" />
        </colgroup>

        <thead>
          <tr>
            <th>CNPJ</th>
            <th onClick={() => handleSort("sigla")} className="sortable-header">
              Sigla {getSortIcon("sigla")}
            </th>
            <th
              onClick={() => handleSort("solicitacaoLabel")}
              className="sortable-header"
            >
              Solicitação {getSortIcon("solicitacaoLabel")}
            </th>
            <th
              onClick={() => handleSort("situacao")}
              className="sortable-header"
            >
              Situação {getSortIcon("situacao")}
            </th>
            <th onClick={() => handleSort("envio")} className="sortable-header">
              Envio dos Comprovantes {getSortIcon("envio")}
            </th>
            <th
              onClick={() => handleSort("solicitacaoCorrecao")}
              className="sortable-header"
            >
              Solicitação Correção {getSortIcon("solicitacaoCorrecao")}
            </th>
            <th
              onClick={() => handleSort("correcao")}
              className="sortable-header"
            >
              Correção dos Comprovantes {getSortIcon("correcao")}
            </th>
            <th
              onClick={() => handleSort("envioFormulario")}
              className="sortable-header"
            >
              Envio do Formulário {getSortIcon("envioFormulario")}
            </th>
            <th className="text-center">Ações</th>
          </tr>
        </thead>

        <tbody>
          {currentItems.length === 0 && (
            <tr>
              <td colSpan={9} className="text-center text-muted py-4">
                Nenhum comprovante encontrado.
              </td>
            </tr>
          )}

          {currentItems.map((item) => {
            const disabled = !item.acaoHabilitada;
            const { icon, className } = getSituationConfig(item.situacao);
            const editTooltip = disabled
              ? TOOLTIP_AGUARDANDO_SINDICATO
              : "Conferir comprovantes";

            return (
              <tr key={item.rowKey}>
                <td className="cnpj-col">{formatCnpj(item.cnpj) || "-"}</td>
                <td>
                  <OverlayTrigger
                    placement="top"
                    delay={{ show: 250, hide: 120 }}
                    onEnter={() =>
                      item.idSindicato && handleTooltip(item.idSindicato)
                    }
                    overlay={(props) => (
                      <Tooltip id="tooltip-sindicato" {...props}>
                        {item.nome || tooltipContent || "Carregando..."}
                      </Tooltip>
                    )}
                  >
                    <span className="tooltip-target">{item.sigla || "-"}</span>
                  </OverlayTrigger>
                </td>
                <td>{item.solicitacaoLabel || "—"}</td>
                <td>
                  <div className="situation-cell">
                    <span className={`situation-icon ${className}`}>{icon}</span>
                    <span className="situation-text">
                      {item.situacao || "-"}
                    </span>
                  </div>
                </td>
                <td>{formatDateTimeBR(item.envio)}</td>
                <td>{formatDateTimeBR(item.solicitacaoCorrecao)}</td>
                <td>{formatDateTimeBR(item.correcao)}</td>
                <td>{formatDateTimeBR(item.envioFormulario)}</td>
                <td>
                  <div className="action-buttons d-flex gap-2 justify-content-center">
                    <OverlayTrigger
                      placement="top"
                      overlay={<Tooltip>{editTooltip}</Tooltip>}
                    >
                      <span>
                        <Button
                          className="btn-edit"
                          size="sm"
                          onClick={() => handleEdit(item)}
                          disabled={disabled}
                          style={
                            disabled
                              ? { opacity: 0.35, cursor: "not-allowed" }
                              : undefined
                          }
                        >
                          <FaEdit />
                        </Button>
                      </span>
                    </OverlayTrigger>

                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => onOpenHistory(item)}
                      title="Ver histórico"
                    >
                      <FaClock />
                    </Button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    </div>
  );
};

export default PaymentTable;
